import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../../api/client";
import { useMe } from "../../auth/useMe";
import { HierarchyApi, type Branch, type Constituency, type District, type Ward } from "./api";

/**
 * Hierarchy admin pages.
 *
 *   /app/admin/hierarchy                                    — Districts
 *   /app/admin/hierarchy/d/:did                             — Constituencies in a district
 *   /app/admin/hierarchy/d/:did/c/:cid                      — Wards in a constituency
 *   /app/admin/hierarchy/d/:did/c/:cid/w/:wid               — Branches in a ward
 *
 * Each card carries a View button that drills into the next level. Records
 * are append-only by design; only superadmins see Delete buttons, and the
 * API still refuses to delete a parent that has children.
 */
export function Hierarchy() {
  return (
    <Routes>
      <Route index element={<DistrictsScreen />} />
      <Route path="d/:did" element={<ConstituenciesScreen />} />
      <Route path="d/:did/c/:cid" element={<WardsScreen />} />
      <Route path="d/:did/c/:cid/w/:wid" element={<BranchesScreen />} />
    </Routes>
  );
}

// ---------- Districts ----------

function DistrictsScreen() {
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);

  const districts = useQuery({
    queryKey: ["districts"],
    queryFn: HierarchyApi.listDistricts,
  });
  const create = useMutation({
    mutationFn: HierarchyApi.createDistrict,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["districts"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: HierarchyApi.deleteDistrict,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["districts"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div>
      <header style={{ marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Hierarchy</h1>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Districts → Constituencies → Wards → Branches. Records are kept permanently — they're referenced by historical activity.
        </p>
      </header>

      {error && <div role="alert" className="error">{error}</div>}

      <div style={{ marginTop: 16 }}>
        <CreateForm
          labels={{ name: "District name (e.g. Lusaka)", code: "Short code (e.g. LSK)" }}
          onSubmit={(v) => { setError(null); create.mutate(v); }}
          submitting={create.isPending}
        />
      </div>

      {districts.isLoading && <p>Loading…</p>}
      {districts.error && (
        <div className="error">
          Couldn't load: {districts.error instanceof ApiError
            ? `HTTP ${districts.error.status} — ${districts.error.message}`
            : (districts.error as Error).message}
        </div>
      )}

      <div className="card-grid">
        {districts.data?.items.map((d: District) => (
          <HierarchyCard
            key={d.id}
            title={d.name}
            subtitle={d.code}
            viewTo={`d/${d.id}`}
            viewLabel="View constituencies"
            canDelete={isSuperadmin}
            onDelete={() => {
              if (window.confirm(`Delete "${d.name}"? Refused if any constituencies still belong to it.`))
                remove.mutate(d.id);
            }}
          />
        ))}
        {districts.data?.items.length === 0 && !districts.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>
            No districts yet. Add one above to get started.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Constituencies ----------

function ConstituenciesScreen() {
  const { did = "" } = useParams();
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);

  const district = useQuery({
    queryKey: ["district", did],
    queryFn: () => HierarchyApi.listDistricts().then((r) => r.items.find((d) => d.id === did) ?? null),
  });
  const constituencies = useQuery({
    queryKey: ["constituencies", did],
    queryFn: () => HierarchyApi.listConstituenciesInDistrict(did),
    enabled: !!did,
  });
  const create = useMutation({
    mutationFn: (body: { name: string; code: string; lat?: number; lng?: number }) =>
      HierarchyApi.createConstituency({ ...body, districtId: did }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["constituencies", did] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: HierarchyApi.deleteConstituency,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["constituencies", did] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div>
      <Link to=".." className="muted" style={{ fontSize: 13 }}>← All districts</Link>
      <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 600 }}>
        Constituencies in {district.data?.name ?? did}
      </h1>
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>
        Constituencies belong to this district. Add or open one to manage its wards.
      </p>

      {error && <div role="alert" className="error">{error}</div>}

      <div style={{ marginTop: 16 }}>
        <CreateForm
          labels={{ name: "Constituency name (e.g. Mansa Central)", code: "Short code (e.g. MNS-CTR)" }}
          onSubmit={(v) => { setError(null); create.mutate(v); }}
          submitting={create.isPending}
        />
      </div>

      {constituencies.isLoading && <p>Loading…</p>}

      <div className="card-grid">
        {constituencies.data?.items.map((c: Constituency) => (
          <HierarchyCard
            key={c.id}
            title={c.name}
            subtitle={c.code}
            viewTo={`c/${c.id}`}
            viewLabel="View wards"
            canDelete={isSuperadmin}
            onDelete={() => {
              if (window.confirm(`Delete "${c.name}"? Refused if any wards still belong to it. Audit log keeps the record.`))
                remove.mutate(c.id);
            }}
          />
        ))}
        {constituencies.data?.items.length === 0 && !constituencies.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>
            No constituencies in this district yet. Add one above.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Wards ----------

function WardsScreen() {
  const { did = "", cid = "" } = useParams();
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);

  const constituency = useQuery({
    queryKey: ["constituency-info", did, cid],
    queryFn: () => HierarchyApi.listConstituenciesInDistrict(did).then((r) => r.items.find((c) => c.id === cid) ?? null),
    enabled: !!did && !!cid,
  });
  const wards = useQuery({
    queryKey: ["wards", cid],
    queryFn: () => HierarchyApi.listWards(cid),
    enabled: !!cid,
  });
  const create = useMutation({
    mutationFn: (body: { name: string; code: string }) => HierarchyApi.createWard(cid, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wards", cid] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: HierarchyApi.deleteWard,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wards", cid] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div>
      <Link to=".." className="muted" style={{ fontSize: 13 }}>← Back to constituencies</Link>
      <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 600 }}>
        Wards in {constituency.data?.name ?? cid}
      </h1>
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>
        Wards belong to this constituency. Add or open one to manage its branches.
      </p>

      {error && <div role="alert" className="error">{error}</div>}

      <div style={{ marginTop: 16 }}>
        <CreateForm
          labels={{ name: "Ward name", code: "Short code" }}
          onSubmit={(v) => { setError(null); create.mutate(v); }}
          submitting={create.isPending}
        />
      </div>

      {wards.isLoading && <p>Loading wards…</p>}

      <div className="card-grid">
        {wards.data?.items.map((w: Ward) => (
          <HierarchyCard
            key={w.id}
            title={w.name}
            subtitle={w.code}
            viewTo={`w/${w.id}`}
            viewLabel="View branches"
            canDelete={isSuperadmin}
            onDelete={() => {
              if (window.confirm(`Delete "${w.name}"? Refused if any branches still belong to it.`))
                remove.mutate(w.id);
            }}
          />
        ))}
        {wards.data?.items.length === 0 && !wards.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>
            No wards in this constituency yet. Add one above.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Branches ----------

function BranchesScreen() {
  const { cid = "", wid = "" } = useParams();
  const qc = useQueryClient();
  const { isSuperadmin } = useMe();
  const [error, setError] = useState<string | null>(null);

  const ward = useQuery({
    queryKey: ["ward-info", cid, wid],
    queryFn: () => HierarchyApi.listWards(cid).then((r) => r.items.find((w) => w.id === wid) ?? null),
    enabled: !!cid && !!wid,
  });
  const branches = useQuery({
    queryKey: ["branches", wid],
    queryFn: () => HierarchyApi.listBranches(wid),
    enabled: !!wid,
  });
  const create = useMutation({
    mutationFn: (body: { name: string; code: string }) => HierarchyApi.createBranch(wid, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["branches", wid] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: HierarchyApi.deleteBranch,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["branches", wid] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div>
      <Link to=".." className="muted" style={{ fontSize: 13 }}>← Back to wards</Link>
      <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 600 }}>
        Branches in {ward.data?.name ?? wid}
      </h1>
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>
        Branches are the smallest unit. Operational records (cases, intel, POI, tasks) attach here.
      </p>

      {error && <div role="alert" className="error">{error}</div>}

      <div style={{ marginTop: 16 }}>
        <CreateForm
          labels={{ name: "Branch name", code: "Short code" }}
          onSubmit={(v) => { setError(null); create.mutate(v); }}
          submitting={create.isPending}
        />
      </div>

      {branches.isLoading && <p>Loading branches…</p>}

      <div className="card-grid">
        {branches.data?.items.map((b: Branch) => (
          <HierarchyCard
            key={b.id}
            title={b.name}
            subtitle={b.code}
            canDelete={isSuperadmin}
            onDelete={() => {
              if (window.confirm(`Delete "${b.name}"?`)) remove.mutate(b.id);
            }}
          />
        ))}
        {branches.data?.items.length === 0 && !branches.isLoading && (
          <div className="muted" style={{ padding: 16, gridColumn: "1 / -1" }}>
            No branches in this ward yet. Add one above.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Reusable ----------

function HierarchyCard({
  title, subtitle, viewTo, viewLabel, canDelete, onDelete,
}: {
  title: string;
  subtitle: string;
  viewTo?: string;
  viewLabel?: string;
  canDelete: boolean;
  onDelete: () => void;
}) {
  const nav = useNavigate();
  return (
    <div
      className="list-card"
      style={{ cursor: viewTo ? "pointer" : "default" }}
      onClick={(e) => {
        if (!viewTo) return;
        if ((e.target as HTMLElement).closest("button")) return;
        nav(viewTo);
      }}
    >
      <div className="list-card-title">{title}</div>
      <div className="list-card-snippet"><code>{subtitle}</code></div>
      <div className="list-card-foot" style={{ borderTop: "none", padding: 0 }}>
        {viewTo && (
          <Link to={viewTo} style={{ fontSize: 12 }}>{viewLabel ?? "View"}</Link>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            style={{ fontSize: 11, padding: "2px 8px", marginLeft: 8 }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function slugify(s: string): string {
  return s
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9_-]/g, "")
    .slice(0, 32);
}

function CreateForm({
  labels, onSubmit, submitting,
}: {
  labels: { name: string; code: string };
  onSubmit: (v: { name: string; code: string; lat?: number; lng?: number }) => void;
  submitting: boolean;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanCode = slugify(code) || slugify(name);
    if (!cleanName || !cleanCode) return;
    const latNum = lat ? Number(lat) : undefined;
    const lngNum = lng ? Number(lng) : undefined;
    if ((lat && Number.isNaN(latNum)) || (lng && Number.isNaN(lngNum))) return;
    onSubmit({
      name: cleanName,
      code: cleanCode,
      lat: latNum,
      lng: lngNum,
    });
    setName(""); setCode(""); setLat(""); setLng("");
  }

  const codePreview = slugify(code) || (name ? slugify(name) : "");

  return (
    <div>
      <form className="create" onSubmit={submit}>
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder={labels.name}
          autoCapitalize="words"
        />
        <input
          value={code} onChange={(e) => setCode(e.target.value)}
          placeholder={`${labels.code} — leave blank to auto-generate`}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
        />
        <button type="submit" disabled={submitting || !name.trim()}>{submitting ? "Adding…" : "Add"}</button>
      </form>
      <div className="row-3" style={{ marginTop: 6 }}>
        <input
          value={lat} onChange={(e) => setLat(e.target.value)}
          placeholder="Latitude (e.g. -11.1992)"
          spellCheck={false} autoCorrect="off"
        />
        <input
          value={lng} onChange={(e) => setLng(e.target.value)}
          placeholder="Longitude (e.g. 28.8943)"
          spellCheck={false} autoCorrect="off"
        />
        <span />
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        {codePreview
          ? <>The code will be saved as: <code>{codePreview}</code>. Coordinates are optional — they let the entry plot on the Map.</>
          : "Code can have letters, numbers, hyphens. Coordinates are optional — they place this entry on the Map page."}
      </div>
    </div>
  );
}
